/**
 * Ce que l'enfant a écrit de sa journée, côté parents (web-pwa §6.7).
 *
 * Demandé par Hedi le 2 septembre 2026 : « je veux aussi avoir un écran avec ce
 * qui a été rentré dans le journal pour les parents ».
 *
 * **C'est l'autre face de la règle 4, pas une exception.** La règle interdit aux
 * parents le verbatim d'une séance : ce que l'enfant dit au tuteur lui
 * appartient. Le journal est l'inverse exact — l'enfant l'écrit *pour* ses
 * parents, l'écran d'écriture le lui dit au moment où il écrit, et c'est cette
 * promesse-là qui doit être tenue des deux côtés. Les deux textes ne partagent
 * aucune table, précisément pour que l'un ne puisse jamais glisser dans l'autre.
 *
 * **Le texte est montré tel quel.** Pas de résumé, pas de reformulation : ce
 * sont ses mots, et un parent qui lit une paraphrase ne lit plus son enfant.
 *
 * La lecture est journalisée avant que le handler ne tourne (`core-api` §9,
 * `child.journal.read`) — comme toute lecture parentale, et pour la même raison :
 * chaque parent doit pouvoir voir ce que l'autre a consulté.
 */

import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { JournalDuJour } from "@/api/schema";
import { enClair } from "@/ui/dates";
import { etiquette } from "@/ui/matieres";
import { Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

export function JournalScreen() {
  const { childId = "" } = useParams();
  const [journaux, setJournaux] = useState<JournalDuJour[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const recu = await call("journalDeLEnfant", {
          params: { child_id: childId },
          query: { limit: 30 },
        });
        if (!cancelled) setJournaux(recu.journaux ?? []);
      } catch (failure) {
        if (!cancelled) {
          setError(failure instanceof ApiError ? failure.message : "Chargement impossible.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [childId]);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!journaux) return <Loading />;

  return (
    <Screen title="Le journal de la journée">
      <p className="notice">
        Écrit par votre enfant, pour vous, tous les soirs. Ce sont ses mots, montrés tels quels.
        Ce qu'il dit au tuteur pendant une séance ne remonte pas ici.
      </p>

      {journaux.length === 0 ? (
        <Card>
          <Empty>Rien d'écrit pour l'instant.</Empty>
        </Card>
      ) : (
        journaux.map((journal) => (
          <section key={journal.le} className="journal__jour">
            <h2 className="journal__date">{enClair(journal.le)}</h2>
            <JourneeDite journal={journal} />
          </section>
        ))
      )}

      <p className="deep-link">
        <Link to={`/parents/enfant/${childId}`}>← Revenir à la fiche</Link>
      </p>
    </Screen>
  );
}

function JourneeDite({ journal }: { journal: JournalDuJour }) {
  const dites = (journal.lignes ?? []).filter(
    (ligne) => (ligne.fait ?? "").trim() || (ligne.coince ?? "").trim(),
  );

  // **« Rien à dire » et « rien écrit » ne se ressemblent pas, et c'est voulu.**
  // Le premier est une réponse, le second un silence — et le second n'apparaît
  // pas du tout dans cette liste. Confondre les deux ferait lire un oubli là où
  // l'enfant a répondu, ou l'inverse.
  if (dites.length === 0) {
    return (
      <Card>
        <p className="muted">
          {journal.nothing_to_say
            ? "« Rien de spécial aujourd'hui. »"
            : "Journée déclarée, sans détail."}
        </p>
        <AnnulesDuJour journal={journal} />
      </Card>
    );
  }

  return (
    <>
      {dites.map((ligne, rang) => (
        <Card key={`${ligne.creneau}-${ligne.matiere}-${rang}`} title={etiquette(ligne.matiere)}>
          {ligne.creneau ? <p className="muted small">{ligne.creneau}</p> : null}
          {ligne.propose?.trim() ? (
            <p className="muted small">Annoncé au cahier de textes : {ligne.propose}</p>
          ) : null}
          {ligne.fait?.trim() ? (
            <p>
              <span className="field__label">Fait : </span>
              {ligne.fait}
            </p>
          ) : null}
          {ligne.coince?.trim() ? (
            <p>
              <span className="field__label">A coincé : </span>
              {ligne.coince}
            </p>
          ) : null}
        </Card>
      ))}
      <AnnulesDuJour journal={journal} />
    </>
  );
}

/** Les cours qui n'ont pas eu lieu — ils expliquent un trou dans la journée. */
function AnnulesDuJour({ journal }: { journal: JournalDuJour }) {
  const annules = (journal.lignes ?? []).filter((ligne) => ligne.annule);
  if (annules.length === 0) return null;
  return (
    <p className="muted small">
      Cours annulés : {annules.map((ligne) => etiquette(ligne.matiere)).join(", ")}.
    </p>
  );
}
